import { useEffect, useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useUpdates } from 'providers/UpdatesProvider/UpdatesProvider'
import { UpdateModal as UpdateModalType } from 'hooks/queries/updateModals/useUpdateModals'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useAvatarCatalog } from 'hooks/queries/brandKits/useAvatarCatalog'
import { CreateAvatarVariables } from 'hooks/queries/brandKits/useCreateBrandKitAvatar'
import { useOnboardAvatars } from 'hooks/queries/brandKits/useOnboardAvatars'
import { useRecommendAvatars } from 'hooks/queries/brandKits/useRecommendAvatars'
import { AddAvatarModal } from 'screens/ContentPreferences/tabs/AvatarsTab/AddAvatarModal'
import { IntroStep } from './steps/IntroStep'
import { ChooseAvatarsStep } from './steps/ChooseAvatarsStep'
import { ScheduleAvatarVideoStep, ScheduleConfig } from './steps/ScheduleAvatarVideoStep'
import { AllSetStep } from './steps/AllSetStep'
import { AvatarDraft, draftFromCatalog } from './types'

type Step = 'intro' | 'choose' | 'schedule' | 'done'

const variablesFor = (draft: AvatarDraft): CreateAvatarVariables => ({
  name: draft.name,
  persona_note: draft.personaNote,
  age: draft.age,
  ethnicity: draft.ethnicity,
  enabled: true,
  voice_preset: draft.voicePreset ?? null,
  voice_media_file_id: draft.voiceMediaFileId ?? null,
  ...(draft.catalogKey ? { catalog_key: draft.catalogKey } : { media_file_id: draft.mediaFileId }),
})

export const AiAvatarAnnouncementModal = ({ updateModal }: { updateModal: UpdateModalType }) => {
  const { openModal, closeModal } = useModals()
  const { markModalAsViewed } = useUpdates()
  const workspaceId = useCurrentWorkspaceId()
  const { data: brandProfile } = useBrandProfile()
  const brandKitId = brandProfile?.brand_kit?.id
  const { data: catalog = [] } = useAvatarCatalog({ brandKitId })
  const onboardAvatars = useOnboardAvatars({ brandKitId })

  const [step, setStep] = useState<Step>('intro')
  const [drafts, setDrafts] = useState<AvatarDraft[]>([])
  const [isCommitting, setIsCommitting] = useState(false)

  const recommendation = useRecommendAvatars({ brandKitId, enabled: step === 'choose' })

  useEffect(() => {
    if (!recommendation.data || catalog.length === 0 || drafts.length > 0) return

    const picks = recommendation.data
      .map((key) => catalog.find((entry) => entry.key === key))
      .filter((entry): entry is NonNullable<typeof entry> => !!entry)
      .map(draftFromCatalog)

    if (picks.length > 0) setDrafts(picks)
  }, [recommendation.data, catalog, drafts.length])

  const handleDismiss = () => {
    markModalAsViewed(updateModal.id)
    closeModal()
  }

  const handleAddAvatar = () => {
    openModal(
      AddAvatarModal,
      { brandKitId, onAdd: (draft: AvatarDraft) => setDrafts((prev) => [...prev, draft]) },
      { newStack: true },
    )
  }

  const handleEditAvatar = (draft: AvatarDraft) => {
    openModal(
      AddAvatarModal,
      {
        brandKitId,
        draft,
        onSave: (updated: AvatarDraft) =>
          setDrafts((prev) => prev.map((existing) => (existing.localId === updated.localId ? updated : existing))),
      },
      { newStack: true },
    )
  }

  const handleCommit = async (config: ScheduleConfig) => {
    setIsCommitting(true)
    try {
      await onboardAvatars.mutateAsync({
        avatars: drafts.map(variablesFor),
        posts_per_week: config.count,
        week_days: config.weekDays,
        account_ids: config.accountIds,
      })
      setStep('done')
    } catch (error) {
      reportError(error)
      showErrorMessageToast('Something went wrong setting up your avatar videos. Please try again.')
    } finally {
      setIsCommitting(false)
    }
  }

  if (step === 'choose') {
    return (
      <ChooseAvatarsStep
        drafts={drafts}
        onDraftsChange={setDrafts}
        onAddAvatar={handleAddAvatar}
        onEditAvatar={handleEditAvatar}
        onBack={() => setStep('intro')}
        onContinue={() => setStep('schedule')}
        isCreating={false}
        isLoading={recommendation.isLoading}
      />
    )
  }

  if (step === 'schedule') {
    return (
      <ScheduleAvatarVideoStep onBack={() => setStep('choose')} onCommit={handleCommit} isCommitting={isCommitting} />
    )
  }

  if (step === 'done') {
    return <AllSetStep workspaceId={workspaceId} onDone={handleDismiss} />
  }

  return (
    <IntroStep
      photoUrls={catalog.slice(0, 4).map((entry) => entry.photo_url)}
      onContinue={() => setStep('choose')}
      onDismiss={handleDismiss}
    />
  )
}
